<template>
    <div class="admin-album-list">
        <div class="page-header">
            <h2>专辑管理</h2>
            <el-button type="primary" @click="showCreateDialog">
                <el-icon>
                    <Plus />
                </el-icon> 新建专辑
            </el-button>
        </div>

        <el-table :data="albums" style="width: 100%" v-loading="loading">
            <el-table-column prop="id" label="ID" width="80" />
            <el-table-column prop="title" label="标题" />
            <el-table-column prop="description" label="简介" show-overflow-tooltip />
            <el-table-column label="创建时间" width="180">
                <template #default="scope">
                    {{ formatDate(scope.row.CreatedAt) }}
                </template>
            </el-table-column>
            <el-table-column label="操作" width="250">
                <template #default="scope">
                    <el-button size="small" @click="handleEditContent(scope.row)">内容管理</el-button>
                    <el-button size="small" type="primary" @click="handleEditInfo(scope.row)">编辑信息</el-button>
                    <el-button size="small" type="danger" @click="handleDelete(scope.row)">删除</el-button>
                </template>
            </el-table-column>
        </el-table>

        <!-- Create/Edit Dialog -->
        <el-dialog v-model="dialogVisible" :title="isEdit ? '编辑专辑信息' : '新建专辑'" width="500px">
            <el-form :model="form" label-width="80px">
                <el-form-item label="标题">
                    <el-input v-model="form.title" placeholder="请输入专辑名称" />
                </el-form-item>
                <el-form-item label="简介">
                    <el-input v-model="form.description" type="textarea" placeholder="请输入简介" />
                </el-form-item>
                <el-form-item label="封面图">
                    <el-input v-model="form.coverImage" placeholder="封面图片 URL" />
                </el-form-item>
                <el-form-item label="省份">
                    <el-input v-model="form.province" placeholder="例如：北京、江苏" />
                </el-form-item>
                <el-row :gutter="20">
                    <el-col :span="12">
                        <el-form-item label="浏览量">
                            <el-input-number v-model="form.views" :min="0" />
                        </el-form-item>
                    </el-col>
                    <el-col :span="12">
                        <el-form-item label="下载量">
                            <el-input-number v-model="form.downloads" :min="0" />
                        </el-form-item>
                    </el-col>
                </el-row>
                <el-divider content-position="left">作者信息 (可选)</el-divider>
                <el-form-item label="作者名称">
                    <el-input v-model="form.authorName" placeholder="默认：刷题网官方" />
                </el-form-item>
                <el-form-item label="作者头像">
                    <el-input v-model="form.authorAvatar" placeholder="头像 URL" />
                </el-form-item>
                <el-form-item label="作者认证">
                    <el-input v-model="form.authorDescription" placeholder="例如：官方认证 / 资深教师" />
                </el-form-item>
            </el-form>
            <template #footer>
                <span class="dialog-footer">
                    <el-button @click="dialogVisible = false">取消</el-button>
                    <el-button type="primary" @click="submitForm">确定</el-button>
                </span>
            </template>
        </el-dialog>
    </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { Plus } from '@element-plus/icons-vue'
import { getAlbums, createAlbum, updateAlbum, deleteAlbum } from '@/utils/api'
import { ElMessage, ElMessageBox } from 'element-plus'

const router = useRouter()
const albums = ref([])
const loading = ref(false)
const dialogVisible = ref(false)
const isEdit = ref(false)
const form = ref({
    id: null,
    title: '',
    description: '',
    coverImage: '',
    province: '',
    views: 0,
    downloads: 0,
    authorName: '',
    authorAvatar: '',
    authorDescription: ''
})

onMounted(() => {
    fetchAlbums()
})

const fetchAlbums = async () => {
    loading.value = true
    try {
        const res = await getAlbums()
        albums.value = res.data
    } catch (error) {
        ElMessage.error('获取专辑列表失败')
    } finally {
        loading.value = false
    }
}

const formatDate = (dateStr) => {
    if (!dateStr) return '-'
    return new Date(dateStr).toLocaleString()
}

const showCreateDialog = () => {
    isEdit.value = false
    form.value = {
        title: '', description: '', coverImage: '', province: '',
        views: 0, downloads: 0,
        authorName: '', authorAvatar: '', authorDescription: ''
    }
    dialogVisible.value = true
}

const handleEditInfo = (row) => {
    isEdit.value = true
    form.value = { ...row }
    dialogVisible.value = true
}

const handleEditContent = (row) => {
    router.push(`/admin/albums/${row.id}/edit`)
}

const handleDelete = (row) => {
    ElMessageBox.confirm(
        '确定要删除该专辑吗？所有章节和内容关联将被删除（试卷本身不会被删除）。',
        '警告',
        {
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            type: 'warning',
        }
    ).then(async () => {
        try {
            await deleteAlbum(row.id)
            ElMessage.success('删除成功')
            fetchAlbums()
        } catch (error) {
            ElMessage.error('删除失败')
        }
    })
}

const submitForm = async () => {
    if (!form.value.title) {
        ElMessage.warning('请输入标题')
        return
    }

    try {
        if (isEdit.value) {
            await updateAlbum(form.value.id, form.value)
            ElMessage.success('更新成功')
        } else {
            await createAlbum(form.value)
            ElMessage.success('创建成功')
        }
        dialogVisible.value = false
        fetchAlbums()
    } catch (error) {
        ElMessage.error(isEdit.value ? '更新失败' : '创建失败')
    }
}
</script>

<style scoped>
.admin-album-list {
    padding: 20px;
    background: #fff;
    border-radius: 8px;
}

.page-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
}
</style>
